import React from "react";
import { toast } from "sonner";
import { Instagram, Facebook, Sparkles } from "lucide-react";
import { SOCIAL, buildShareCaption } from "../../lib/social";

/**
 * Show a friendly post-action toast that:
 *  1) thanks the user,
 *  2) reminds them to tag CampCompass UK on socials,
 *  3) offers 1-tap copy of a pre-filled caption + 1-tap open Instagram/Facebook.
 *
 * Use anywhere a user has just done something share-worthy:
 *   - posted a review
 *   - saved a trip
 *   - submitted a photo
 *
 * Usage:
 *   showTagUsToast({
 *     headline: "Review posted — thank you!",
 *     captionInput: { kind: "review", name: "Great Langdale", rating: 5 },
 *   });
 */
export function showTagUsToast({ headline, captionInput }) {
  const caption = buildShareCaption(captionInput);
  toast.custom(
    (id) => (
      <div
        className="bg-white border-2 border-[#111] shadow-[0_8px_0_0_#111] p-4 max-w-sm w-[calc(100vw-2rem)]"
        data-testid="tag-us-toast"
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-[#fff4ee] inline-flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4 text-[#ff4500]" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-base mb-1">{headline}</div>
            <p className="text-xs text-[#555] leading-snug mb-3">
              Loved it? Share on Instagram or Facebook — tag <strong className="mono text-[#ff4500]">{SOCIAL.instagram.handle}</strong> so we can repost you 🏕️
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(caption);
                    toast.success("Caption copied — paste in Instagram");
                  } catch {
                    toast.error("Couldn't copy. Long-press to copy manually.");
                  }
                  window.open(SOCIAL.instagram.url, "_blank", "noopener");
                  toast.dismiss(id);
                }}
                className="text-xs mono uppercase tracking-wider px-3 py-2 bg-gradient-to-tr from-[#f09433] via-[#e6683c] via-50% to-[#bc1888] text-white inline-flex items-center gap-1.5 hover:scale-[1.02] transition-transform"
                data-testid="tag-us-share-instagram"
              >
                <Instagram className="w-3.5 h-3.5" strokeWidth={2.5} /> Instagram
              </button>
              <button
                onClick={() => {
                  const fbUrl = "https://www.facebook.com/sharer/sharer.php?u=" +
                    encodeURIComponent(window.location.href) +
                    "&quote=" + encodeURIComponent(caption);
                  window.open(fbUrl, "_blank", "noopener,width=600,height=520");
                  toast.dismiss(id);
                }}
                className="text-xs mono uppercase tracking-wider px-3 py-2 bg-[#1877F2] text-white inline-flex items-center gap-1.5 hover:scale-[1.02] transition-transform"
                data-testid="tag-us-share-facebook"
              >
                <Facebook className="w-3.5 h-3.5" strokeWidth={2.5} /> Facebook
              </button>
              <button
                onClick={() => toast.dismiss(id)}
                className="text-xs mono uppercase tracking-wider px-3 py-2 border hairline hover:bg-[#fafafa]"
                data-testid="tag-us-dismiss"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      </div>
    ),
    { duration: 8000 },
  );
}
